import { useState, useEffect, useContext, createContext } from 'react';

// Theme Context
const ThemeContext = createContext();

// Theme Provider Component
export const ThemeProvider = ({ children }) => {
	const [theme, setTheme] = useState('light');
	const [themeColor, setThemeColor] = useState('#1890ff');

	// Load theme from localStorage on initial render
	useEffect(() => {
		const savedTheme = localStorage.getItem('fitT-theme') || 'light';
		const savedColor = localStorage.getItem('fitT-theme-color') || '#1890ff';
		
		setTheme(savedTheme);
		setThemeColor(savedColor);
		
		// Apply theme immediately
		applyTheme(savedTheme, savedColor);
	}, []);

	// Apply theme to document
	const applyTheme = (themeMode, color) => {
		const root = document.documentElement;
		
		// Remove existing theme classes
		document.body.classList.remove('light-theme', 'dark-theme');
		
		// Apply theme mode
		document.body.classList.add(`${themeMode}-theme`);
		
		// Apply CSS custom properties for colors
		if (themeMode === 'dark') {
			root.style.setProperty('--bg-color', '#0d0d0d');
			root.style.setProperty('--card-bg', '#1a1a1a');
			root.style.setProperty('--text-color', '#ffffff');
			root.style.setProperty('--text-secondary', '#b3b3b3');
			root.style.setProperty('--border-color', '#3a3a3a');
			root.style.setProperty('--bg-light', '#262626');
		} else {
			root.style.setProperty('--bg-color', '#f5f5f5');
			root.style.setProperty('--card-bg', '#ffffff');
			root.style.setProperty('--text-color', '#000000');
			root.style.setProperty('--text-secondary', '#666666');
			root.style.setProperty('--border-color', '#d9d9d9');
			root.style.setProperty('--bg-light', '#fafafa');
		}
		
		// Apply primary color
		const colorRgb = hexToRgb(color);
		const softBackground = themeMode === 'dark'
			? `rgba(${colorRgb}, 0.22)`
			: `rgba(${colorRgb}, 0.14)`;
		const strongSoftBackground = themeMode === 'dark'
			? `rgba(${colorRgb}, 0.3)`
			: `rgba(${colorRgb}, 0.22)`;
		const actionGradientEnd = adjustColor(color, -18);

		root.style.setProperty('--primary-color', color);
		root.style.setProperty('--primary-color-hover', adjustColor(color, 20));
		root.style.setProperty('--primary-color-active', adjustColor(color, -20));
		root.style.setProperty('--primary-color-rgb', colorRgb);
		root.style.setProperty('--primary-soft-bg', softBackground);
		root.style.setProperty('--primary-soft-bg-strong', strongSoftBackground);
		root.style.setProperty('--primary-action-gradient', `linear-gradient(135deg, ${color} 0%, ${actionGradientEnd} 100%)`);
		root.style.setProperty('--primary-action-border', `rgba(${colorRgb}, 0.28)`);
		
		// Apply gradient colors
		const gradientEnd = adjustColor(color, -40);
		root.style.setProperty('--gradient-primary', `linear-gradient(135deg, ${color} 0%, ${gradientEnd} 100%)`);
		root.style.setProperty('--gradient-primary-light', `linear-gradient(135deg, ${color}1a 0%, ${gradientEnd}0f 100%)`);
		
		// Update Ant Design theme variables
		updateAntdTheme(themeMode, color);
	};

	const hexToRgb = (color) => {
		const normalized = color.replace('#', '');
		const expanded = normalized.length === 3
			? normalized.split('').map((value) => `${value}${value}`).join('')
			: normalized;
		const parsed = parseInt(expanded, 16);
		return `${(parsed >> 16) & 255}, ${(parsed >> 8) & 255}, ${parsed & 255}`;
	};

	// Helper function to adjust color brightness
	const adjustColor = (color, amount) => {
		const num = parseInt(color.replace('#', ''), 16);
		const amt = Math.round(2.55 * amount);
		const R = (num >> 16) + amt;
		const G = (num >> 8 & 0x00FF) + amt;
		const B = (num & 0x0000FF) + amt;
		return '#' + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
			(G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
			(B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
	};

	// Update Ant Design theme
	const updateAntdTheme = (themeMode, color) => {
		// Create or update style element for Ant Design overrides
		let styleEl = document.getElementById('antd-theme-override');
		if (!styleEl) {
			styleEl = document.createElement('style');
			styleEl.id = 'antd-theme-override';
			document.head.appendChild(styleEl);
		}

		const colorRgb = hexToRgb(color);
		const menuSelectedBackground = themeMode === 'dark'
			? `rgba(${colorRgb}, 0.22)`
			: `rgba(${colorRgb}, 0.14)`;
		const optionSelectedBackground = themeMode === 'dark'
			? `rgba(${colorRgb}, 0.28)`
			: `rgba(${colorRgb}, 0.16)`;

		const darkStyles = themeMode === 'dark' ? `
			/* Sidebar and Menu Dark Theme */
			.ant-layout-sider {
				background: #1a1a1a !important;
				border-right: 1px solid #3a3a3a !important;
			}
			.ant-menu {
				background: #1a1a1a !important;
				color: #ffffff !important;
				border-right: none !important;
			}
			.ant-menu-item {
				color: #b3b3b3 !important;
				margin-bottom: 4px !important;
			}
			.ant-menu-item:hover {
				background: #262626 !important;
				color: ${color} !important;
			}
			.ant-menu-item-selected {
				background: ${menuSelectedBackground} !important;
				color: ${color} !important;
			}
			.ant-menu-submenu-title:hover {
				background: #262626 !important;
				color: ${color} !important;
			}
			
			/* Page Header Dark Theme */
			.ant-page-header {
				background: #1a1a1a !important;
				border-bottom: 1px solid #3a3a3a !important;
			}
			
			/* Table Dark Theme */
			.ant-table {
				background: #1a1a1a !important;
				color: #ffffff !important;
			}
			.ant-table-thead > tr > th {
				background: #262626 !important;
				color: ${color} !important;
				border-bottom: 1px solid #3a3a3a !important;
				font-weight: 700;
			}
			.ant-table-tbody > tr > td {
				border-bottom: 1px solid #3a3a3a !important;
				background: #1a1a1a !important;
				color: #b3b3b3 !important;
			}
			.ant-table-tbody > tr:hover > td {
				background: rgba(58, 58, 58, 0.3) !important;
			}
			
			/* Card Dark Theme */
			.ant-card {
				background: #1a1a1a !important;
				border-color: #3a3a3a !important;
				box-shadow: 0 2px 8px rgba(0, 0, 0, 0.45) !important;
			}
			.ant-card .ant-card-head {
				background: #1a1a1a !important;
				border-bottom: 1px solid #3a3a3a !important;
				color: #ffffff !important;
			}
			.ant-card .ant-card-body {
				background: #1a1a1a !important;
				color: #ffffff !important;
			}
			
			/* Form Controls Dark Theme */
			.ant-input,
			.ant-select-selector,
			.ant-picker {
				background: #262626 !important;
				border-color: #3a3a3a !important;
				color: #ffffff !important;
			}
			.ant-input::placeholder,
			.ant-select-selection-placeholder {
				color: #b3b3b3 !important;
			}
			.ant-select-dropdown {
				background: #1a1a1a !important;
				border-color: #3a3a3a !important;
			}
			.ant-select-item {
				color: #ffffff !important;
			}
			.ant-select-item:hover {
				background: #262626 !important;
			}
			
			/* Button Dark Theme */
			.ant-btn-default {
				background: #262626 !important;
				border-color: #3a3a3a !important;
				color: #ffffff !important;
			}
			.ant-btn-default:hover {
				background: #3a3a3a !important;
				border-color: ${color} !important;
				color: ${color} !important;
			}
			
			/* Tabs Dark Theme */
			.ant-tabs {
				color: #ffffff !important;
			}
			.ant-tabs-nav {
				background: #1a1a1a !important;
			}
			.ant-tabs-nav::before {
				border-bottom-color: #3a3a3a !important;
			}
			.ant-tabs-tab {
				color: #b3b3b3 !important;
			}
			.ant-tabs-tab:hover {
				color: ${color} !important;
			}
			.ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
				color: ${color} !important;
			}
			.ant-tabs-content-holder,
			.ant-tabs-tabpane {
				background: #1a1a1a !important;
				color: #ffffff !important;
			}
		` : '';

		styleEl.textContent = `
			${darkStyles}
			.ant-btn-primary {
				background: ${color} !important;
				border-color: ${color} !important;
			}
			.ant-btn-primary:hover {
				background: ${adjustColor(color, 20)} !important;
				border-color: ${adjustColor(color, 20)} !important;
			}
			.ant-btn-primary:active {
				background: ${adjustColor(color, -20)} !important;
				border-color: ${adjustColor(color, -20)} !important;
			}
			.ant-switch-checked {
				background: ${color} !important;
			}
			.ant-menu-item-selected::after {
				background: ${color} !important;
				border-right: 3px solid ${color} !important;
			}
			.ant-tabs-ink-bar {
				background: ${color} !important;
			}
			.ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
				background: ${optionSelectedBackground} !important;
				color: ${color} !important;
			}
			.ant-pagination-item-active {
				background: ${color} !important;
				border-color: ${color} !important;
			}
			.ant-checkbox-checked .ant-checkbox-inner {
				background-color: ${color} !important;
				border-color: ${color} !important;
			}
			.ant-radio-button-wrapper-checked {
				background: ${color} !important;
				border-color: ${color} !important;
			}
			.ant-progress-bg {
				background-color: ${color} !important;
			}
			.ant-badge-count {
				background-color: ${color} !important;
			}
			a {
				color: ${color} !important;
			}
		`;
	};

	// Toggle between light and dark theme
	const toggleTheme = () => {
		const newTheme = theme === 'light' ? 'dark' : 'light';
		setTheme(newTheme);
		localStorage.setItem('fitT-theme', newTheme);
		applyTheme(newTheme, themeColor);
	};

	// Update theme color
	const updateThemeColor = (color) => {
		setThemeColor(color);
		localStorage.setItem('fitT-theme-color', color);
		applyTheme(theme, color);
	};

	const value = {
		theme,
		themeColor,
		toggleTheme,
		setThemeColor: updateThemeColor,
		applyTheme
	};

	return (
		<ThemeContext.Provider value={value}>
			{children}
		</ThemeContext.Provider>
	);
};

// Custom hook to use theme context
export const useTheme = () => {
	const context = useContext(ThemeContext);
	if (!context) {
		throw new Error('useTheme must be used within a ThemeProvider');
	}
	return context;
};